import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  SimpleGrid,
  Stack,
  Tag,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

/* --------------------------
   Small reusable components
-------------------------- */

function Section({ title, subtitle, children }) {
  const titleColor = useColorModeValue("#0F172A", "white");
  const subColor = useColorModeValue("gray.600", "gray.300");
  const divider = useColorModeValue("rgba(2,6,23,0.08)", "rgba(148,163,184,0.18)");

  return (
    <Box py={{ base: 4, md: 6 }}>
      <Container maxW="1200px">
        <Stack spacing={2} mb={{ base: 6, md: 8 }}>
          <Heading fontSize={{ base: "2xl", md: "3xl" }} color={titleColor} letterSpacing="tight">
            {title}
          </Heading>
          {subtitle ? (
            <Text fontSize={{ base: "md", md: "lg" }} color={subColor} maxW="900px">
              {subtitle}
            </Text>
          ) : null}
        </Stack>

        {children}

        <Divider mt={{ base: 10, md: 14 }} borderColor={divider} />
      </Container>
    </Box>
  );
}

function InfoCard({ label, text }) {
  const cardBg = useColorModeValue("white", "rgba(255,255,255,0.04)");
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const bodyColor = useColorModeValue("gray.700", "gray.200");

  return (
    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={border}
      borderRadius="22px"
      p={{ base: 6, md: 7 }}
      boxShadow={useColorModeValue("0 12px 30px rgba(0,0,0,0.08)", "none")}
    >
      <HStack spacing={3} mb={3}>
        <Tag
          size="lg"
          borderRadius="full"
          bg={useColorModeValue("rgba(6,182,212,0.10)", "rgba(6,182,212,0.18)")}
          color={useColorModeValue("#0E7490", "#67E8F9")}
          fontWeight="900"
          letterSpacing="0.06em"
        >
          {label}
        </Tag>
      </HStack>

      <Text fontSize={{ base: "md", md: "lg" }} color={bodyColor} textAlign="left" lineHeight={1.75}>
        {text}
      </Text>
    </Box>
  );
}

function StatPill({ k, v }) {
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const bg = useColorModeValue("rgba(255,255,255,0.70)", "rgba(255,255,255,0.04)");
  const kColor = useColorModeValue("gray.600", "gray.300");
  const vColor = useColorModeValue("#0F172A", "white");

  return (
    <Box border="1px solid" borderColor={border} bg={bg} borderRadius="18px" p={4}>
      <Text fontSize="xs" letterSpacing="0.12em" fontWeight="900" color={kColor}>
        {k.toUpperCase()}
      </Text>
      <Text fontSize="sm" fontWeight="800" color={vColor} mt={1}>
        {v}
      </Text>
    </Box>
  );
}

/* --------------------------
   Inline Carousel (no imports)
   - works for desktop and mobile
   - no cropping: objectFit="contain"
-------------------------- */
function InlineCarousel({
  images,
  title,
  subtitle,
  autoPlay = true,
  intervalMs = 4500,
  aspect = { base: "16 / 10", md: "16 / 8" },
}) {
  const [idx, setIdx] = useState(0);

  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const panelBg = useColorModeValue("white", "rgba(255,255,255,0.03)");
  const titleColor = useColorModeValue("#0F172A", "white");
  const subColor = useColorModeValue("gray.600", "gray.300");
  const dotOff = useColorModeValue("rgba(2,6,23,0.18)", "rgba(255,255,255,0.22)");
  const dotOn = "#06B6D4";

  const n = images?.length || 0;

  useEffect(() => {
    if (!autoPlay || n <= 1) return;
    const t = setInterval(() => setIdx((v) => (v + 1) % n), intervalMs);
    return () => clearInterval(t);
  }, [autoPlay, intervalMs, n]);

  const prev = () => setIdx((v) => (v - 1 + n) % n);
  const next = () => setIdx((v) => (v + 1) % n);

  if (!n) return null;

  const current = images[idx];

  return (
    <Box
      border="1px solid"
      borderColor={border}
      borderRadius="26px"
      overflow="hidden"
      bg={panelBg}
      boxShadow={useColorModeValue("0 14px 40px rgba(0,0,0,0.10)", "none")}
    >
      <Box p={{ base: 4, md: 6 }}>
        <Heading fontSize={{ base: "lg", md: "xl" }} color={titleColor} letterSpacing="tight">
          {title}
        </Heading>
        {subtitle ? (
          <Text color={subColor} mt={1}>
            {subtitle}
          </Text>
        ) : null}
      </Box>

      <Divider borderColor={border} />

      <Box position="relative" p={{ base: 3, md: 4 }}>
        <Box
          border="1px solid"
          borderColor={border}
          borderRadius="22px"
          bg={useColorModeValue("rgba(2,6,23,0.02)", "rgba(255,255,255,0.03)")}
          overflow="hidden"
        >
          <Box
            w="100%"
            sx={{
              aspectRatio: aspect, // modern browsers; keeps frame stable
            }}
          >
            <Flex w="100%" h="100%" align="center" justify="center" p={{ base: 3, md: 5 }}>
              <Image
                src={typeof current === "string" ? current : current.src}
                alt={typeof current === "string" ? `slide-${idx + 1}` : current.alt || `slide-${idx + 1}`}
                w="100%"
                h="100%"
                objectFit="contain" // ✅ no cropping
                draggable={false}
              />
            </Flex>
          </Box>
        </Box>

        {/* Arrows */}
        {n > 1 && (
          <>
            <IconButton
              aria-label="Previous"
              icon={<ChevronLeftIcon />}
              position="absolute"
              left={{ base: 2, md: 4 }}
              top="50%"
              transform="translateY(-50%)"
              onClick={prev}
              variant="ghost"
              bg={useColorModeValue("rgba(255,255,255,0.70)", "rgba(15,23,42,0.55)")}
              _hover={{ filter: "brightness(0.97)" }}
              borderRadius="999px"
            />
            <IconButton
              aria-label="Next"
              icon={<ChevronRightIcon />}
              position="absolute"
              right={{ base: 2, md: 4 }}
              top="50%"
              transform="translateY(-50%)"
              onClick={next}
              variant="ghost"
              bg={useColorModeValue("rgba(255,255,255,0.70)", "rgba(15,23,42,0.55)")}
              _hover={{ filter: "brightness(0.97)" }}
              borderRadius="999px"
            />
          </>
        )}

        {/* Dots */}
        {n > 1 && (
          <HStack justify="center" spacing={2} pt={4}>
            {images.map((_, i) => (
              <Box
                key={i}
                w="8px"
                h="8px"
                borderRadius="999px"
                bg={i === idx ? dotOn : dotOff}
                cursor="pointer"
                onClick={() => setIdx(i)}
              />
            ))}
          </HStack>
        )}
      </Box>
    </Box>
  );
}

/* --------------------------
   Home Page
-------------------------- */
export default function HomePage() {
  const isMobile = useSelector((state) => state.windowSize.isMobile);
  const location = useLocation();

  // EN/FR base prefix
  const lang = location.pathname.startsWith("/fr") ? "fr" : "en";
  const base = lang === "fr" ? "/fr" : "/en";

  // Palette aligned to your navbar/footer
  const accent = "#06B6D4";
  const accent2 = "#22D3EE";
  const titleColor = useColorModeValue("#0F172A", "white");
  const subColor = useColorModeValue("gray.600", "gray.300");
  const softBorder = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const pageBg = useColorModeValue("white", "#0B1220");
  const panelBg = useColorModeValue("white", "rgba(255,255,255,0.03)");

  const heroBg = useColorModeValue(
    "radial-gradient(1000px 450px at 15% 10%, rgba(6,182,212,0.18), transparent 60%), radial-gradient(900px 450px at 85% 30%, rgba(34,211,238,0.14), transparent 55%), linear-gradient(180deg, rgba(15,23,42,0.02), rgba(15,23,42,0.00))",
    "radial-gradient(1000px 450px at 15% 10%, rgba(6,182,212,0.20), transparent 60%), radial-gradient(900px 450px at 85% 30%, rgba(34,211,238,0.14), transparent 55%), linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.00))"
  );

  // ✅ Put your real images here (public folder recommended)
  // Example: public/home/highlight_1.png  -> "home/highlight_1.png"
  const highlights = useMemo(
    () => [
      { src: "/LabImage5.png", alt: "TRAVERSAL highlight 1" },
      { src: "/LabImage2.png", alt: "TRAVERSAL highlight 2" },
      { src: "/LabImage3.png", alt: "TRAVERSAL highlight 3" },
    ],
    []
  );

  const labPictures = useMemo(
    () => [
      { src: "/LabImage4.png", alt: "Lab 1" },
      { src: "/lab_picture.jpg", alt: "Lab 2" },
      { src: "/LabImage2.png", alt: "Lab 3" },
    ],
    []
  );

  return (
    <Box bg={pageBg}>
      {/* HERO */}
      <Box
        pt={{ base: 8, md: 12 }}
        pb={{ base: 10, md: 14 }}
        bg={heroBg}
        borderBottom="1px solid"
        borderColor={softBorder}
      >
        <Container maxW="1200px">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 8, md: 12 }} alignItems="center">
            <Stack spacing={5}>
              <HStack spacing={2} flexWrap="wrap">
                <Tag
                  size="lg"
                  borderRadius="full"
                  bg={useColorModeValue("white", "rgba(255,255,255,0.06)")}
                  border="1px solid"
                  borderColor={softBorder}
                  fontWeight="800"
                >
                  University of Ottawa
                </Tag>

                <Tag
                  size="lg"
                  borderRadius="full"
                  bg={useColorModeValue("rgba(6,182,212,0.12)", "rgba(6,182,212,0.18)")}
                  color={useColorModeValue("#0E7490", "#67E8F9")}
                  fontWeight="900"
                >
                  TRAVERSAL
                </Tag>
              </HStack>

              <Heading
                color={titleColor}
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                lineHeight={1.05}
                letterSpacing="tight"
              >
                TRAVERSAL:{" "}
                <Box as="span" bgClip="text" bgGradient={`linear(to-r, ${accent}, ${accent2})`}>
                  Trustworthy Autonomous Mobility
                </Box>{" "}
                Research
              </Heading>

              <Text fontSize={{ base: "md", md: "lg" }} color={subColor} maxW="62ch" lineHeight={1.8}>
                We advance trustworthy AI, resilient connectivity, and edge intelligence to enable safer autonomous
                transportation—bridging research, testbeds, and real-world deployment.
              </Text>

              <HStack spacing={3} flexWrap="wrap">
                <Button
                  as={RouterLink}
                  to={`/publications`}
                  bg={accent}
                  color="#001018"
                  fontWeight="900"
                  size="lg"
                  borderRadius="16px"
                  _hover={{ filter: "brightness(0.95)" }}
                >
                  Explore Publications
                </Button>

                <Button
                  as={RouterLink}
                  to={`/seminars`}
                  variant="outline"
                  borderColor={softBorder}
                  size="lg"
                  borderRadius="16px"
                  fontWeight="900"
                >
                  Seminars
                </Button>

                <Button
                  as={RouterLink}
                  to={`/contact-us`}
                  variant="ghost"
                  size="lg"
                  borderRadius="16px"
                  fontWeight="900"
                  color={titleColor}
                  _hover={{ bg: useColorModeValue("rgba(2,6,23,0.05)", "rgba(255,255,255,0.06)") }}
                >
                  Collaborate →
                </Button>
              </HStack>

              <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3} pt={2}>
                <StatPill k="Focus" v="Trustworthy autonomy" />
                <StatPill k="Methods" v="AI • Networking • Edge" />
                <StatPill k="Impact" v="Safety • Reliability" />
              </SimpleGrid>
            </Stack>

            {/* Right visual: no cropping */}
            <Box
              borderRadius="26px"
              overflow="hidden"
              border="1px solid"
              borderColor={softBorder}
              bg={panelBg}
              boxShadow={useColorModeValue("0 18px 50px rgba(0,0,0,0.12)", "none")}
            >
              

              <Box p={{ base: 3, md: 4 }}>
                <Image src="lab_image1.png" alt="TRAVERSAL" w="100%" h="auto" objectFit="contain" />
              </Box>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* HIGHLIGHTS (inline carousel) */}
      <Box py={{ base: 10, md: 12 }}>
        <Container maxW="1200px">
          <InlineCarousel
            images={highlights}
            title="Highlights"
            subtitle="Projects, partnerships, and experimental work from the lab."
            autoPlay={!isMobile} // optional: disable autoplay on mobile
            intervalMs={4500}
            aspect={{ base: "16 / 10", md: "16 / 8" }}
          />
        </Container>
      </Box>

      {/* MISSION / VISION / GOAL */}
      <Section title="Mission, Vision & Goal" subtitle="">
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <InfoCard
            label="Mission"
            text={
              <>
                <b>CREATE TRAVERSAL </b> is dedicated to endowing HQP with both technical and soft skills, promoting interdisciplinary collaboration, and propelling 
                Canada to the forefront of autonomous technology leadership through autonomous vehicles (AVs) and uncrewed aerial vehicles (UAVs).
              </>
            }
          />
          <InfoCard
            label="Vision"
            text={
              <>
                CREATE TRAVERSAL envisions Canada at the forefront of innovation in trustworthy, safe, secure, smart, and responsible autonomous technologies.
              </>
            }
          />
          <InfoCard
            label="Goal"
            text={
              <>
                To strengthen Canada's connected and autonomous vehicles (CAV) workforce and talent pipeline through strategically designed training 
                modules and industrial internships in collaboration between academia and industry so as to overcome skill shortages.
              </>
            }
          />
        </SimpleGrid>
      </Section>

      {/* WHAT WE DO */}
      <Section title="What We Do" subtitle="Core research themes that define the project direction.">
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={5}>
          {[
            { t: "Trustworthy AI", d: "Robust perception, prediction, and decision support under uncertainty." },
            { t: "Edge Intelligence", d: "Low-latency inference, semantic compression, and edge-cloud deployment." },
            { t: "Resilient Connectivity", d: "V2X, network orchestration, and reliability-aware communication." },
            { t: "Safety & Compliance", d: "Risk estimation, constraints, and policy-aligned autonomy behaviors." },
          ].map((x) => (
            <Box
              key={x.t}
              border="1px solid"
              borderColor={softBorder}
              borderRadius="22px"
              p={6}
              bg={panelBg}
              boxShadow={useColorModeValue("0 10px 24px rgba(0,0,0,0.07)", "none")}
            >
              <Text fontWeight="900" color={titleColor} fontSize="lg" mb={2}>
                {x.t}
              </Text>
              <Text color={subColor} fontSize="md" lineHeight={1.75}>
                {x.d}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </Section>

      {/* LAB PICTURES (second inline carousel) */}
      <Box pb={{ base: 14, md: 16 }}>
        <Container maxW="1200px">
          <InlineCarousel
            images={labPictures}
            title="Lab Pictures"
            subtitle="Demo videos, testbeds, and research updates."
            autoPlay={!isMobile}
            intervalMs={5000}
            aspect={{ base: "16 / 10", md: "16 / 8" }}
          />
        </Container>
      </Box>

      {/* FINAL CTA */}
      <Box
        py={{ base: 12, md: 16 }}
        bg={useColorModeValue("rgba(2,6,23,0.03)", "rgba(255,255,255,0.03)")}
        borderTop="1px solid"
        borderColor={softBorder}
      >
        <Container maxW="1200px">
          <Box
            border="1px solid"
            borderColor={softBorder}
            bg={panelBg}
            borderRadius="26px"
            p={{ base: 7, md: 10 }}
            boxShadow={useColorModeValue("0 16px 44px rgba(0,0,0,0.10)", "none")}
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 6, md: 10 }} alignItems="center">
              <Stack spacing={3}>
                <Heading color={titleColor} fontSize={{ base: "xl", md: "2xl" }}>
                  Interested in collaboration?
                </Heading>
                <Text color={subColor} fontSize={{ base: "md", md: "lg" }} lineHeight={1.75}>
                  We welcome partnerships across academia, industry, and the public sector—contact us to explore joint
                  research, datasets, testbeds, and technology transfer.
                </Text>
              </Stack>

              <Flex justify={{ base: "flex-start", md: "flex-end" }} gap={3} flexWrap="wrap">
                <Button
                  as={RouterLink}
                  to={`/contact-us`}
                  bg={accent}
                  color="#001018"
                  fontWeight="900"
                  size="lg"
                  borderRadius="16px"
                  _hover={{ filter: "brightness(0.95)" }}
                >
                  Contact
                </Button>
                <Button
                  as={RouterLink}
                  to={`/about-us`}
                  variant="outline"
                  borderColor={softBorder}
                  size="lg"
                  borderRadius="16px"
                  fontWeight="900"
                >
                  Meet the Team
                </Button>
              </Flex>
            </SimpleGrid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
